const GRID_COLUMNS = 12;
const GRID_ROWS = 8;
const MAX_SAMPLES = 220_000;
const emptyZone = () => ({ count: 0, luma: 0, r: 0, g: 0, b: 0 });
const clamp01 = (value) => Math.max(0, Math.min(1, value));
const round = (value, digits = 4) => Number(value.toFixed(digits));
const pixelLuma = (r, g, b) => 0.2126 * r + 0.7152 * g + 0.0722 * b;
function addPixel(zone, r, g, b) {
    zone.count++;
    zone.r += r;
    zone.g += g;
    zone.b += b;
    zone.luma += pixelLuma(r, g, b);
}
function combine(zones, indices) {
    return indices.reduce((total, index) => {
        const zone = zones[index];
        total.count += zone.count;
        total.luma += zone.luma;
        total.r += zone.r;
        total.g += zone.g;
        total.b += zone.b;
        return total;
    }, emptyZone());
}
const meanLuma = (zone) => zone.luma / Math.max(1, zone.count);
function colorBias(zone) {
    if (!zone.count)
        return 'unknown';
    const r = zone.r / zone.count;
    const g = zone.g / zone.count;
    const b = zone.b / zone.count;
    if (Math.max(r, g, b) - Math.min(r, g, b) < 16)
        return 'neutral';
    if (g > r * 1.18 && g > b * 1.12)
        return 'green';
    if (b > r * 1.18 && b > g * 1.1)
        return 'blue';
    if (r > g * 1.12 && b > g * 1.08)
        return 'magenta';
    if (r > b * 1.18 && r >= g * 1.02)
        return 'warm';
    if (b > r * 1.12)
        return 'cool';
    return 'neutral';
}
function screenPixel(type, r, g, b) {
    if (type === 'green')
        return g >= 35 && g - Math.max(r, b) >= 18 && g >= r * 1.2 && g >= b * 1.08;
    if (type === 'blue')
        return b >= 35 && b - Math.max(r, g) >= 18 && b >= r * 1.2 && b >= g * 1.06;
    return false;
}
function upstreamScreen(context) {
    const result = context.analyzerResults.get('screen-quality');
    const metrics = result?.metrics;
    return metrics?.screen_type === 'green' || metrics?.screen_type === 'blue' ? metrics.screen_type : 'unknown';
}
function arrowFor(source) {
    const origins = {
        front: { x: 0.5, y: 0.95 }, front_left: { x: 0.08, y: 0.85 },
        front_right: { x: 0.92, y: 0.85 }, side_left: { x: 0.02, y: 0.5 },
        side_right: { x: 0.98, y: 0.5 }, top: { x: 0.5, y: 0.02 },
        bottom: { x: 0.5, y: 0.98 }, back_left: { x: 0.12, y: 0.08 },
        back_right: { x: 0.88, y: 0.08 },
    };
    const origin = origins[source.direction];
    if (!origin)
        return undefined;
    return {
        type: 'arrow', points: [origin, { x: 0.5, y: 0.5 }],
        label: `Apparent ${source.role}: ${source.direction}`,
        color: source.role === 'rim' || source.role === 'backlight' ? '#ff9fef' : '#ffe16b',
        opacity: source.confidence, stroke_width: source.role === 'key' ? 4 : 2,
    };
}
/** Estimates apparent light roles from a single 2D frame; it does not locate physical fixtures. */
export class MultiSourceLightingAnalyzer {
    id = 'lighting-setup';
    label = 'Lighting Setup';
    analyze(frame, context) {
        const { width, height, data } = frame;
        const stride = Math.max(1, Math.ceil(Math.sqrt((width * height) / MAX_SAMPLES)));
        const screenType = upstreamScreen(context);
        const subjectZones = Array.from({ length: 9 }, emptyZone);
        const screenCells = Array.from({ length: GRID_COLUMNS * GRID_ROWS }, emptyZone);
        const frameCells = Array.from({ length: GRID_COLUMNS * GRID_ROWS }, emptyZone);
        const fullFrame = emptyZone();
        const subject = emptyZone();
        const screen = emptyZone();
        for (let y = 0; y < height; y += stride) {
            for (let x = 0; x < width; x += stride) {
                const offset = (y * width + x) * 4;
                const r = data[offset] ?? 0;
                const g = data[offset + 1] ?? 0;
                const b = data[offset + 2] ?? 0;
                addPixel(fullFrame, r, g, b);
                const gridColumn = Math.min(GRID_COLUMNS - 1, Math.floor((x / width) * GRID_COLUMNS));
                const gridRow = Math.min(GRID_ROWS - 1, Math.floor((y / height) * GRID_ROWS));
                addPixel(frameCells[gridRow * GRID_COLUMNS + gridColumn], r, g, b);
                if (screenPixel(screenType, r, g, b)) {
                    addPixel(screen, r, g, b);
                    addPixel(screenCells[gridRow * GRID_COLUMNS + gridColumn], r, g, b);
                }
                else {
                    addPixel(subject, r, g, b);
                    const column = Math.min(2, Math.floor((x / width) * 3));
                    const row = Math.min(2, Math.floor((y / height) * 3));
                    addPixel(subjectZones[row * 3 + column], r, g, b);
                }
            }
        }
        const left = combine(subjectZones, [0, 3, 6]);
        const center = combine(subjectZones, [1, 4, 7]);
        const right = combine(subjectZones, [2, 5, 8]);
        const top = combine(subjectZones, [0, 1, 2]);
        const middle = combine(subjectZones, [3, 4, 5]);
        const bottom = combine(subjectZones, [6, 7, 8]);
        const leftLuma = meanLuma(left);
        const rightLuma = meanLuma(right);
        const topLuma = meanLuma(top);
        const middleLuma = meanLuma(middle);
        const bottomLuma = meanLuma(bottom);
        const brightSide = leftLuma >= rightLuma ? left : right;
        const darkSide = leftLuma >= rightLuma ? right : left;
        const brightLuma = meanLuma(brightSide);
        const darkLuma = meanLuma(darkSide);
        const lightingRatio = subject.count ? brightLuma / Math.max(1, darkLuma) : null;
        let keyDirection = 'unknown';
        if (subject.count) {
            if (topLuma > Math.max(middleLuma, bottomLuma) * 1.22)
                keyDirection = 'top';
            else if (leftLuma > rightLuma * 1.55)
                keyDirection = 'side_left';
            else if (rightLuma > leftLuma * 1.55)
                keyDirection = 'side_right';
            else if (leftLuma > rightLuma * 1.15)
                keyDirection = 'front_left';
            else if (rightLuma > leftLuma * 1.15)
                keyDirection = 'front_right';
            else
                keyDirection = 'front';
        }
        const keySoftness = lightingRatio === null ? 'unknown'
            : lightingRatio > 2.2 ? 'hard' : lightingRatio > 1.35 ? 'medium' : 'soft';
        const sources = [];
        const addSource = (source) => {
            const detected = { id: `light_${String(sources.length + 1).padStart(2, '0')}`, ...source };
            sources.push(detected);
            return detected;
        };
        if (subject.count) {
            addSource({
                role: 'key', direction: keyDirection, intensity: round(clamp01(brightLuma / 255)),
                softness: keySoftness, color_bias: colorBias(brightSide), affected_region: 'subject',
                confidence: round(clamp01(0.52 + Math.abs(leftLuma - rightLuma) / 180 + subject.count / Math.max(1, fullFrame.count) * 0.2)),
            });
            if (darkLuma > 18 && (lightingRatio ?? 10) <= 4) {
                const fillDirection = leftLuma >= rightLuma ? 'front_right' : 'front_left';
                addSource({
                    role: 'fill', direction: fillDirection, intensity: round(clamp01(darkLuma / 255)),
                    softness: 'soft', color_bias: colorBias(darkSide), affected_region: 'subject',
                    confidence: round(clamp01(0.38 + darkLuma / Math.max(1, brightLuma) * 0.45)),
                });
            }
        }
        const subjectMean = meanLuma(subject);
        const rimSides = { left: emptyZone(), right: emptyZone(), top: emptyZone() };
        if (screenType !== 'unknown' && subject.count) {
            const distance = Math.max(1, stride * 2);
            for (let y = 0; y < height; y += stride) {
                for (let x = 0; x < width; x += stride) {
                    const offset = (y * width + x) * 4;
                    const r = data[offset] ?? 0;
                    const g = data[offset + 1] ?? 0;
                    const b = data[offset + 2] ?? 0;
                    if (screenPixel(screenType, r, g, b) || pixelLuma(r, g, b) < subjectMean + 24)
                        continue;
                    const touchesScreen = [[-distance, 0], [distance, 0], [0, -distance], [0, distance]].some(([dx, dy]) => {
                        const nx = x + dx;
                        const ny = y + dy;
                        if (nx < 0 || nx >= width || ny < 0 || ny >= height)
                            return false;
                        const neighbor = (ny * width + nx) * 4;
                        return screenPixel(screenType, data[neighbor] ?? 0, data[neighbor + 1] ?? 0, data[neighbor + 2] ?? 0);
                    });
                    if (!touchesScreen)
                        continue;
                    const side = y < height * 0.3 ? rimSides.top : x < width / 2 ? rimSides.left : rimSides.right;
                    addPixel(side, r, g, b);
                }
            }
        }
        const rimCandidates = Object.entries(rimSides).filter(([, zone]) => zone.count >= Math.max(2, subject.count * 0.003));
        rimCandidates.sort((a, b) => b[1].count - a[1].count);
        rimCandidates.slice(0, 2).forEach(([side, zone]) => {
            const direction = side === 'left' ? 'back_left' : side === 'right' ? 'back_right' : 'top';
            addSource({
                role: 'rim', direction, intensity: round(clamp01(meanLuma(zone) / 255)),
                softness: 'medium', color_bias: colorBias(zone), affected_region: 'edge',
                confidence: round(clamp01(0.45 + zone.count / Math.max(1, subject.count) * 4)),
            });
        });
        const activeScreenCells = screenCells.map((zone, index) => ({ zone, index })).filter(({ zone }) => zone.count);
        const screenMean = meanLuma(screen);
        const screenDeviation = activeScreenCells.length ? Math.sqrt(activeScreenCells.reduce((sum, { zone }) => sum + (meanLuma(zone) - screenMean) ** 2, 0) / activeScreenCells.length) : 0;
        const backgroundUniformity = screen.count ? clamp01(1 - screenDeviation / 65) : 0;
        if (screen.count) {
            const screenSoftness = backgroundUniformity > 0.82 ? 'soft' : backgroundUniformity > 0.6 ? 'medium' : 'hard';
            if (backgroundUniformity >= 0.72) {
                addSource({
                    role: 'background_screen_light', direction: 'background', intensity: round(clamp01(screenMean / 255)),
                    softness: screenSoftness, color_bias: screenType, affected_region: 'screen',
                    confidence: round(clamp01(0.55 + backgroundUniformity * 0.4)),
                });
            }
            else {
                const leftScreen = combine(screenCells, activeScreenCells.filter(({ index }) => index % GRID_COLUMNS < GRID_COLUMNS / 2).map(({ index }) => index));
                const rightScreen = combine(screenCells, activeScreenCells.filter(({ index }) => index % GRID_COLUMNS >= GRID_COLUMNS / 2).map(({ index }) => index));
                [leftScreen, rightScreen].forEach((zone, index) => {
                    if (!zone.count)
                        return;
                    addSource({
                        role: 'background_screen_light', direction: index === 0 ? 'side_left' : 'side_right',
                        intensity: round(clamp01(meanLuma(zone) / 255)), softness: screenSoftness,
                        color_bias: screenType, affected_region: 'screen', confidence: round(clamp01(0.48 + zone.count / Math.max(1, screen.count))),
                    });
                });
            }
        }
        const brightFrameCells = frameCells.filter((zone) => zone.count && meanLuma(zone) > 225);
        if (screenType === 'unknown' && brightFrameCells.length > 0 && brightFrameCells.length <= frameCells.length * 0.15) {
            const practicalZone = brightFrameCells.reduce((total, zone) => combine([total, zone], [0, 1]), emptyZone());
            addSource({
                role: 'practical', direction: 'unknown', intensity: round(clamp01(meanLuma(practicalZone) / 255)),
                softness: 'unknown', color_bias: colorBias(practicalZone), affected_region: 'background', confidence: 0.42,
            });
        }
        const keyCandidates = sources.filter((source) => source.role === 'key' || source.role === 'window' || source.role === 'product_light');
        const fillCandidates = sources.filter((source) => source.role === 'fill' || source.role === 'bounce');
        const rimSources = sources.filter((source) => source.role === 'rim' || source.role === 'backlight');
        const backgroundSources = sources.filter((source) => source.role === 'background_screen_light' || source.role === 'practical');
        const dominant = sources.reduce((best, source) => !best || source.intensity * source.confidence > best.intensity * best.confidence ? source : best, null);
        const sideImbalance = Math.abs(leftLuma - rightLuma) / Math.max(1, Math.max(leftLuma, rightLuma));
        const lightingBalance = clamp01(1 - sideImbalance * 0.65 - (screen.count ? (1 - backgroundUniformity) * 0.35 : 0));
        let pattern = 'unknown';
        const frameMean = meanLuma(fullFrame);
        if (screen.count && backgroundUniformity < 0.68)
            pattern = 'uneven_mixed_sources';
        else if (frameMean > 190)
            pattern = 'high_key_multi_source';
        else if (frameMean < 70)
            pattern = 'low_key_multi_source';
        else if (screenType === 'green')
            pattern = 'green_screen_stage';
        else if (screenType === 'blue')
            pattern = 'blue_screen_stage';
        else if ((lightingRatio ?? 1) < 1.25)
            pattern = 'flat_multi_source';
        else if (keyCandidates.length && fillCandidates.length && rimSources.length)
            pattern = 'classic_three_point';
        else if (keyCandidates.length && (fillCandidates.length || rimSources.length))
            pattern = 'two_point';
        else if (sources.some((source) => source.role === 'practical'))
            pattern = 'practical_motivated';
        const shadowThreshold = Math.max(16, screenMean * 0.16);
        const shadowCells = activeScreenCells.filter(({ zone }) => meanLuma(zone) < screenMean - shadowThreshold);
        const unevenCells = activeScreenCells.filter(({ zone }) => Math.abs(meanLuma(zone) - screenMean) > Math.max(14, screenMean * 0.13));
        const subjectShadowOnScreen = shadowCells.length > 0;
        const warnings = [];
        const warn = (code, message) => {
            warnings.push({ code, severity: 'warning', message, module_id: this.id, frame_number: context.frameNumber });
        };
        if (subjectShadowOnScreen)
            warn('subject_shadow_on_screen', 'Subject shadow detected on chroma screen');
        if (screen.count && backgroundUniformity < 0.78)
            warn('uneven_background_lighting', 'Background lighting is uneven');
        if (keyDirection === 'side_left' || keyDirection === 'side_right')
            warn('lateral_key_light', 'Key light appears too lateral');
        if (pattern === 'flat_multi_source')
            warn('flat_lighting', 'Setup appears too flat');
        if (rimSources.some((source) => source.intensity > 0.85))
            warn('strong_backlight', 'Backlight may be too strong');
        if (pattern === 'low_key_multi_source')
            warn('low_key_quality', 'Low-key lighting may reduce keying quality');
        const overlays = sources.map(arrowFor).filter((overlay) => Boolean(overlay));
        const cellOverlay = ({ index }, label, color) => {
            const column = index % GRID_COLUMNS;
            const row = Math.floor(index / GRID_COLUMNS);
            return { type: 'heatmap', points: [{ x: column / GRID_COLUMNS, y: row / GRID_ROWS }, { x: (column + 1) / GRID_COLUMNS, y: (row + 1) / GRID_ROWS }], label, color, opacity: 0.65, stroke_width: 1, fill: true };
        };
        overlays.push(...shadowCells.map((cell) => cellOverlay(cell, 'Shadow warning zone', '#ff4d6d')));
        overlays.push(...unevenCells.slice(0, 24).map((cell) => cellOverlay(cell, 'Background unevenness heatmap', '#ffb347')));
        const confidence = clamp01(0.25 + (subject.count ? 0.25 : 0) + (screen.count ? 0.25 : 0) + Math.min(0.2, sources.length * 0.04));
        return {
            module_id: this.id, module_name: this.label, confidence: round(confidence),
            frame_number: context.frameNumber, timestamp_ms: context.timestampMs,
            metrics: {
                detected_light_sources: sources,
                dominant_light_source: dominant,
                key_light_candidates: keyCandidates,
                fill_light_candidates: fillCandidates,
                rim_light_candidates: rimSources,
                background_light_sources: backgroundSources,
                lighting_pattern: pattern,
                lighting_balance_score: round(lightingBalance),
                lighting_ratio: lightingRatio === null ? null : round(lightingRatio, 2),
                background_uniformity: round(backgroundUniformity),
                subject_shadow_on_screen: subjectShadowOnScreen,
            },
            warnings,
            overlays,
        };
    }
}
/** Backward-compatible name retained for existing integrations. */
export class LightingSetupAnalyzer extends MultiSourceLightingAnalyzer {
}
//# sourceMappingURL=LightingSetupAnalyzer.js.map